import React, { useEffect, useState, useCallback } from "react";
import AppShell from "../components/AppShell";
import { api } from "../lib/api";
import { fmtDate, fmtMoneyFull } from "../lib/format";
import { toast } from "sonner";
import { PaperPlaneRight, ArrowsClockwise, Wallet, CheckCircle, XCircle, Clock } from "@phosphor-icons/react";

const StatusBadge = ({ status, lastEvent }) => {
  const s = (lastEvent || status || "").toLowerCase();
  let bg = "border-zinc-700 text-zinc-400";
  let icon = <Clock size={11} />;
  if (s === "delivered" || s === "sent") { bg = "border-emerald-500/50 text-emerald-300 bg-emerald-500/10"; icon = <CheckCircle size={11} weight="fill" />; }
  else if (s.includes("open")) { bg = "border-blue-500/50 text-blue-300 bg-blue-500/10"; }
  else if (s.includes("click")) { bg = "border-violet-500/50 text-violet-300 bg-violet-500/10"; }
  else if (s.includes("bounc") || s === "failed" || s.includes("complain")) { bg = "border-red-500/50 text-red-300 bg-red-500/10"; icon = <XCircle size={11} weight="fill" />; }
  return (
    <span className={`inline-flex items-center gap-1 mono text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-sm border ${bg}`}>
      {icon} {lastEvent || status || "pending"}
    </span>
  );
};

export default function OutreachHistory() {
  const [rows, setRows] = useState([]);
  const [balance, setBalance] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    try {
      const [h, b] = await Promise.all([api.get("/outreach/history"), api.get("/batchdata/balance")]);
      setRows(h.data); setBalance(b.data);
    } catch { toast.error("Failed to load"); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const syncAll = async () => {
    setRefreshing(true);
    try {
      const withProvider = rows.filter(r => r.provider_id).slice(0, 25);
      const results = await Promise.allSettled(withProvider.map(r => api.get(`/outreach/${r.id}/delivery`)));
      let updated = 0;
      const map = new Map();
      results.forEach((res, i) => {
        if (res.status === "fulfilled" && res.value?.data?.resend) {
          const ev = res.value.data.resend.last_event;
          if (ev) { map.set(withProvider[i].id, ev); updated++; }
        }
      });
      setRows(rs => rs.map(r => map.has(r.id) ? { ...r, last_event: map.get(r.id) } : r));
      toast.success(`Synced ${updated}/${withProvider.length} deliveries from Resend`);
    } catch { toast.error("Sync failed"); }
    finally { setRefreshing(false); }
  };

  const walletDisplay = () => {
    if (!balance) return "loading…";
    if (balance.status === "funded") return "FUNDED";
    if (balance.status === "depleted") return "DEPLETED";
    if (balance.status === "not_configured") return "no key";
    return "unavailable";
  };
  const walletOk = balance?.status === "funded";

  return (
    <AppShell header={
      <div className="border-b border-zinc-800 px-6 py-4 flex items-center justify-between">
        <div>
          <div className="overline">Outreach History</div>
          <h1 className="text-lg font-semibold flex items-center gap-2"><PaperPlaneRight size={18} /> Every real person contacted</h1>
        </div>
        <div className="flex items-center gap-3">
          <div className={`border rounded-sm px-3 py-2 ${walletOk ? "border-emerald-500/40 bg-emerald-500/8" : "border-amber-500/40 bg-amber-500/8"}`} data-testid="wallet-balance">
            <div className={`overline flex items-center gap-1 ${walletOk ? "text-emerald-400" : "text-amber-400"}`}><Wallet size={11} /> BatchData Wallet</div>
            <div className={`mono text-sm font-semibold ${walletOk ? "text-emerald-300" : "text-amber-200"}`}>{walletDisplay()}</div>
          </div>
          <button onClick={syncAll} disabled={refreshing} data-testid="btn-sync-resend"
            className="text-xs mono uppercase tracking-[0.15em] px-3 py-2 rounded-sm border border-zinc-700 hover:border-zinc-500 flex items-center gap-2 disabled:opacity-50">
            <ArrowsClockwise size={13} className={refreshing ? "animate-spin" : ""} /> Sync Resend Status
          </button>
        </div>
      </div>
    }>
      <div className="p-6">
        <div className="border border-zinc-800 rounded-sm overflow-hidden bg-[#0b0b0e] overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm" data-testid="outreach-table">
            <thead className="bg-[#111114]">
              <tr className="text-[10px] mono uppercase tracking-[0.2em] text-zinc-500">
                <th className="text-left px-4 py-2.5">Property</th>
                <th className="text-left px-4 py-2.5">Owner</th>
                <th className="text-left px-4 py-2.5">Email</th>
                <th className="text-left px-4 py-2.5">Phone</th>
                <th className="text-left px-4 py-2.5">Sent</th>
                <th className="text-left px-4 py-2.5">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={6} className="text-center text-zinc-500 py-10 mono text-xs">No outreach yet. Real leads will populate here the moment offers go out.</td></tr>
              )}
              {rows.map(r => (
                <tr key={r.id} className="border-t border-zinc-800/70 hover:bg-[#111114]" data-testid={`outreach-row-${r.id}`}>
                  <td className="px-4 py-2.5">
                    <div className="text-sm">{r.property || "—"}</div>
                    <div className="text-[10px] mono text-zinc-500 truncate max-w-[280px]">{r.subject}</div>
                  </td>
                  <td className="px-4 py-2.5 text-xs">{r.owner_name || <span className="text-zinc-600">unknown</span>}</td>
                  <td className="px-4 py-2.5 text-xs mono">{r.owner_email || <span className="text-zinc-600">—</span>}</td>
                  <td className="px-4 py-2.5 text-xs mono">{r.owner_phone || <span className="text-zinc-600">—</span>}</td>
                  <td className="px-4 py-2.5 text-xs mono text-zinc-400">{fmtDate(r.created_at)}</td>
                  <td className="px-4 py-2.5"><StatusBadge status={r.status} lastEvent={r.last_event} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-3 text-[10px] mono text-zinc-500">
          Showing {rows.length} outbound message{rows.length === 1 ? "" : "s"} · Click "Sync Resend Status" to pull latest delivery state (delivered / opened / clicked / bounced).
        </div>
      </div>
    </AppShell>
  );
}
